import type { TeacherPreference } from '../../types';

/**
 * Teacher Preferences: Individual teacher scheduling requests
 * These are submitted by teachers and reviewed/approved by timetablers
 */
export const mockTeacherPreferences: TeacherPreference[] = [
  // Approved preferences
  {
    id: 'tp-1',
    teacherId: 'teacher-1',
    teacherName: 'Sarah Johnson',
    type: 'avoid_day',
    description: 'Prefer not to work Fridays (childcare responsibilities)',
    requestedValue: 'friday',
    status: 'approved',
    priority: 'high',
    submittedAt: new Date('2025-01-05'),
    reviewedAt: new Date('2025-01-07'),
    reviewedBy: 'admin-1',
    notes: 'Approved - schedule for Mon-Thu only',
  },
  {
    id: 'tp-2',
    teacherId: 'teacher-15',
    teacherName: 'Michael Brown',
    type: 'preferred_time',
    description: 'Prefer morning periods (most effective teaching time)',
    requestedValue: 'periods_1_2_3',
    status: 'approved',
    priority: 'medium',
    submittedAt: new Date('2025-01-06'),
    reviewedAt: new Date('2025-01-08'),
    reviewedBy: 'admin-1',
    notes: 'Approved - assign to morning slots when possible',
  },
  {
    id: 'tp-3',
    teacherId: 'teacher-7',
    teacherName: 'Emma Wilson',
    type: 'avoid_time',
    description: 'Avoid Period 1 (medical appointment schedule)',
    requestedValue: 'period_1',
    status: 'approved',
    priority: 'high',
    submittedAt: new Date('2025-01-04'),
    reviewedAt: new Date('2025-01-06'),
    reviewedBy: 'admin-1',
    notes: 'Approved due to medical necessity',
  },
  {
    id: 'tp-4',
    teacherId: 'teacher-23',
    teacherName: 'David Lee',
    type: 'consecutive_classes',
    description: 'Prefer consecutive Year 11 Maths lessons for intensive revision',
    requestedValue: 'year_11_maths',
    status: 'approved',
    priority: 'medium',
    submittedAt: new Date('2025-01-08'),
    reviewedAt: new Date('2025-01-09'),
    reviewedBy: 'admin-1',
    notes: 'Approved - beneficial for exam preparation',
  },

  // Pending preferences
  {
    id: 'tp-5',
    teacherId: 'teacher-32',
    teacherName: 'Rachel Green',
    type: 'avoid_day',
    description: 'Request Wednesdays off for professional development course',
    requestedValue: 'wednesday',
    status: 'pending',
    priority: 'medium',
    submittedAt: new Date('2025-01-12'),
    reviewedAt: null,
    reviewedBy: null,
    notes: null,
  },
  {
    id: 'tp-6',
    teacherId: 'teacher-45',
    teacherName: 'Tom Anderson',
    type: 'preferred_time',
    description: 'Prefer afternoon slots (better for practical lab work)',
    requestedValue: 'periods_4_5',
    status: 'pending',
    priority: 'low',
    submittedAt: new Date('2025-01-13'),
    reviewedAt: null,
    reviewedBy: null,
    notes: null,
  },
  {
    id: 'tp-7',
    teacherId: 'teacher-18',
    teacherName: 'Linda Martinez',
    type: 'no_gaps',
    description: 'Request consolidated schedule with minimal gaps',
    requestedValue: 'compact_schedule',
    status: 'pending',
    priority: 'medium',
    submittedAt: new Date('2025-01-14'),
    reviewedAt: null,
    reviewedBy: null,
    notes: null,
  },

  // Rejected preferences
  {
    id: 'tp-8',
    teacherId: 'teacher-50',
    teacherName: 'James Taylor',
    type: 'avoid_day',
    description: 'Request Mondays off for personal reasons',
    requestedValue: 'monday',
    status: 'rejected',
    priority: 'low',
    submittedAt: new Date('2025-01-03'),
    reviewedAt: new Date('2025-01-05'),
    reviewedBy: 'admin-1',
    notes: 'Unable to accommodate - insufficient Monday cover',
  },
  {
    id: 'tp-9',
    teacherId: 'teacher-28',
    teacherName: 'Patricia White',
    type: 'preferred_time',
    description: 'Only teach periods 2-4',
    requestedValue: 'periods_2_3_4',
    status: 'rejected',
    priority: 'medium',
    submittedAt: new Date('2025-01-07'),
    reviewedAt: new Date('2025-01-09'),
    reviewedBy: 'admin-1',
    notes: 'Too restrictive - would impact curriculum delivery',
  },

  // More approved preferences
  {
    id: 'tp-10',
    teacherId: 'teacher-12',
    teacherName: 'Chris Robinson',
    type: 'same_room',
    description: 'Prefer to stay in Science Lab 3 for all lessons (equipment setup)',
    requestedValue: 'room_sci_lab_3',
    status: 'approved',
    priority: 'medium',
    submittedAt: new Date('2025-01-09'),
    reviewedAt: new Date('2025-01-10'),
    reviewedBy: 'admin-1',
    notes: 'Approved - reduces equipment transport time',
  },
  {
    id: 'tp-11',
    teacherId: 'teacher-39',
    teacherName: 'Susan Clark',
    type: 'avoid_consecutive',
    description: 'Avoid teaching the same class twice in one day (need prep time between)',
    requestedValue: 'same_class_separation',
    status: 'approved',
    priority: 'low',
    submittedAt: new Date('2025-01-10'),
    reviewedAt: new Date('2025-01-11'),
    reviewedBy: 'admin-1',
    notes: 'Approved - reasonable pedagogical request',
  },
  {
    id: 'tp-12',
    teacherId: 'teacher-5',
    teacherName: 'Robert Hall',
    type: 'preferred_day',
    description: 'Prefer Year 13 lessons on Tuesdays and Thursdays for consistency',
    requestedValue: 'year_13_tue_thu',
    status: 'approved',
    priority: 'medium',
    submittedAt: new Date('2025-01-11'),
    reviewedAt: new Date('2025-01-12'),
    reviewedBy: 'admin-1',
    notes: 'Approved - helps students with revision planning',
  },
];

export const getTeacherPreferencesByStatus = (status: TeacherPreference['status']): TeacherPreference[] => {
  return mockTeacherPreferences.filter((pref) => pref.status === status);
};

export const getTeacherPreferencesByTeacher = (teacherId: string): TeacherPreference[] => {
  return mockTeacherPreferences.filter((pref) => pref.teacherId === teacherId);
};

export const getPendingTeacherPreferences = (): TeacherPreference[] => {
  return mockTeacherPreferences.filter((pref) => pref.status === 'pending');
};

export const getApprovedTeacherPreferences = (): TeacherPreference[] => {
  return mockTeacherPreferences.filter((pref) => pref.status === 'approved');
};

export const getHighPriorityTeacherPreferences = (): TeacherPreference[] => {
  return mockTeacherPreferences.filter((pref) => pref.priority === 'high');
};
